import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";

/** Finger or mouse signature capture. Returns a PNG data URL. */
export const SignaturePad = ({ onSave, onCancel, saving, testidPrefix = "signature" }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0A0A0A";
  }, []);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const p = e.touches?.[0] || e;
    return { x: p.clientX - rect.left, y: p.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasInk(true);
  };

  const end = () => {
    drawing.current = false;
  };

  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
  };

  return (
    <div data-testid={`${testidPrefix}-pad`}>
      <canvas
        ref={canvasRef}
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
        className="h-40 w-full touch-none border border-dashed border-[#C5A059] bg-[#FFFDF8]"
        data-testid={`${testidPrefix}-canvas`}
      />
      <p className="mt-2 text-xs text-muted-foreground">
        Sign above with your finger or the mouse.
      </p>
      <div className="mt-4 flex gap-2">
        <Button
          type="button"
          variant="outline"
          className="rounded-none"
          onClick={clear}
          data-testid={`${testidPrefix}-clear`}
        >
          Clear
        </Button>
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            className="flex-1 rounded-none"
            onClick={onCancel}
            data-testid={`${testidPrefix}-cancel`}
          >
            Cancel
          </Button>
        )}
        <Button
          type="button"
          disabled={!hasInk || saving}
          className="flex-1 rounded-none bg-[#C5A059] font-semibold text-[#0A0A0A] hover:bg-[#B38F4E]"
          onClick={() => onSave(canvasRef.current.toDataURL("image/png"))}
          data-testid={`${testidPrefix}-save`}
        >
          {saving ? "Saving…" : "Save signature"}
        </Button>
      </div>
    </div>
  );
};
